
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Edit, 
  PlusCircle, 
  Trash2,
  Eye,
} from 'lucide-react';
import { useAdminPropBots, useDeletePropBot } from '@/hooks/use-admin-prop-bots';
import { DeletePropBotDialog } from './DeletePropBotDialog';
import { MobilePropBotCard } from './MobilePropBotCard';
import { PropBotDetailModal } from './PropBotDetailModal';
import { TablePagination } from '@/components/ui/table-pagination';
import { useIsMobile } from '@/hooks/use-mobile';
import { PropBot } from '@/types/prop-bots';

export const PropBotsList = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [botToDelete, setBotToDelete] = React.useState<PropBot | null>(null);
  const [detailModalOpen, setDetailModalOpen] = React.useState(false);
  const [botToView, setBotToView] = React.useState<PropBot | null>(null);
  
  const {
    propBots,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    isLoading,
    isError,
    setPage,
    setPageSize,
  } = useAdminPropBots();

  const deletePropBotMutation = useDeletePropBot();

  // Log prop bots data to debug
  React.useEffect(() => {
    if (propBots && propBots.length > 0) {
      console.log('PropBots data in component:', propBots);
      // Check if propDetails is available
      propBots.forEach(bot => {
        console.log(`Bot ${bot.id} - ${bot.name} - propDetails:`, bot.propDetails);
      });
    }
  }, [propBots]);

  const handleDeleteClick = (bot: PropBot) => {
    setBotToDelete(bot);
    setDeleteDialogOpen(true);
  };

  const handleViewClick = (bot: PropBot) => {
    setBotToView(bot);
    setDetailModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!botToDelete) return;
    
    try {
      await deletePropBotMutation.mutateAsync(botToDelete.id);
      setDeleteDialogOpen(false);
      setBotToDelete(null);
    } catch (error) {
      console.error('Error deleting prop bot:', error);
      // Error is handled by the mutation's onError callback
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBotToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-destructive/10 p-4 rounded-md">
        <p className="text-destructive">Failed to load prop trading bots.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className={`font-medium ${isMobile ? 'text-base' : 'text-lg'}`}>Prop Trading Bots</h2>
        <Button 
          onClick={() => navigate('/admin/prop-trading/new')} 
          size={isMobile ? "default" : "sm"}
          className={isMobile ? "w-full max-w-[140px]" : ""}
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add New
        </Button>
      </div>
      
      {propBots && propBots.length > 0 ? (
        <>
          {isMobile ? (
            // Mobile card layout
            <div className="space-y-3">
              {propBots.map((bot) => (
                <MobilePropBotCard
                  key={bot.id}
                  bot={bot}
                  onDeleteClick={() => handleDeleteClick(bot)}
                  onViewClick={() => handleViewClick(bot)}
                />
              ))}
            </div>
          ) : (
            // Desktop table layout
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Prop Firm</TableHead>
                    <TableHead>Pass Rate</TableHead>
                    <TableHead className="text-right">Min Capital</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propBots.map((bot) => (
                    <TableRow key={bot.id}>
                      <TableCell className="font-medium">{bot.name}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={bot.propDetails?.[0]?.is_instant_account ? 'default' : 'secondary'}
                          className={bot.propDetails?.[0]?.is_instant_account ? 
                            'bg-gradient-to-r from-amber-500 to-yellow-600 text-white' : ''}
                        >
                          {bot.propDetails?.[0]?.is_instant_account ? 'Instant Account' : 'Regular'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={bot.status === 'active' ? 'default' : 'outline'}>
                          {bot.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {/* Access first item in the propDetails array */}
                        {bot.propDetails?.[0]?.prop_firm || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {/* Access first item in the propDetails array */}
                        {bot.propDetails?.[0]?.pass_rate || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Access first item in the propDetails array */}
                        {bot.propDetails?.[0]?.min_capital ? 
                          `$${Number(bot.propDetails[0].min_capital).toLocaleString()}` : 
                          'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => handleViewClick(bot)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => navigate(`/admin/prop-trading/edit/${bot.id}`)}
                            title="Edit Bot"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteClick(bot)}
                            title="Delete Bot"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {totalPages > 1 && (
            <TablePagination 
              currentPage={currentPage}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      ) : (
        <div className="bg-muted/50 rounded-lg p-8 text-center">
          <p className="text-muted-foreground">No prop trading bots found.</p>
          <Button 
            variant="outline" 
            className={`mt-4 ${isMobile ? 'w-full' : ''}`}
            onClick={() => navigate('/admin/prop-trading/new')}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Your First Bot
          </Button>
        </div>
      )}
      
      <DeletePropBotDialog
        isOpen={deleteDialogOpen}
        botId={botToDelete?.id || null}
        onClose={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        botName={botToDelete?.name || "this bot"}
        isLoading={deletePropBotMutation.isPending}
      />
      
      <PropBotDetailModal
        bot={botToView}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setBotToView(null);
        }}
      />
    </div>
  );
};
